/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import React, { useCallback, useMemo } from 'react';
import { Choice } from '@staffino/model';
import { useTranslation } from 'react-i18next';
import {
  theme,
  Box,
  convertResponsiveValue,
} from '@staffino/primitive-controls';
import lerpScoreColors from './lerp-score-colors';
import BoxButton from '../components/box-button';

const colorPalette = [
  '#fc5b02',
  '#fd7402',
  '#fd8d01',
  '#fea501',
  '#febe00',
  '#ffd800',
  '#ccd109',
  '#99cb12',
  '#66c51c',
  '#33bf25',
  '#00b92e',
];

type Props = {
  name: string;
  upperBoundScore: number;
  lowerBoundScore?: number;
  choices: Choice[];
  metric: string;
  skin: string;
  colorStyle?: string;
  value?: number;
  onChange?: (score: number) => void;
} & React.ComponentProps<typeof Box>;

export function titles(
  metric: string,
  lowerBoundScore: number,
  upperBoundScore: number,
  t: (string) => string,
): string[] {
  if (metric === 'CSAT') {
    return [
      t('Very unsatisfied'),
      t('Unsatisfied'),
      t('Neutral'),
      t('Satisfied'),
      t('Very satisfied'),
    ];
  }
  if (upperBoundScore === 7) {
    return [
      t('Strongly disagree'),
      t('Disagree'),
      t('Somewhat disagree'),
      t('Neutral'),
      t('Somewhat agree'),
      t('Agree'),
      t('Strongly agree'),
    ];
  }
  if (upperBoundScore === 6) {
    return [
      t('Strongly disagree'),
      t('Disagree'),
      t('Somewhat disagree'),
      t('Somewhat agree'),
      t('Agree'),
      t('Strongly agree'),
    ];
  }
  if (upperBoundScore === 5) {
    if (!metric) {
      return [
        t('Strongly disagree'),
        t('Disagree'),
        t('Neutral'),
        t('Agree'),
        t('Strongly agree'),
      ];
    }
    if (metric === 'CSAT') {
      return [
        t('Very unsatisfied'),
        t('Unsatisfied'),
        t('Neutral'),
        t('Satisfied'),
        t('Very satisfied'),
      ];
    }
    return [
      t('Very high effort'),
      t('High effort'),
      t('Neutral'),
      t('Low effort'),
      t('Very low effort'),
    ];
  }
  if (upperBoundScore === 4) {
    return [
      t('Strongly disagree'),
      t('Disagree'),
      t('Agree'),
      t('Strongly agree'),
    ];
  }
  if (upperBoundScore === 3) {
    return [t('Disagree'), t('Neutral'), t('Agree')];
  }
  if (upperBoundScore === 2) {
    return [t('Disagree'), t('Agree')];
  }

  return [];
}

const BoxesControl: React.ForwardRefExoticComponent<
  Props & React.RefAttributes<unknown>
> = React.forwardRef(
  (
    {
      name,
      lowerBoundScore = 1,
      upperBoundScore,
      colorStyle,
      choices,
      metric,
      skin,
      value,
      onChange,
      ...rest
    }: Props,
    ref: React.Ref<unknown>,
  ) => {
    const { t } = useTranslation();
    const boxes = useMemo(
      () => lerpScoreColors(lowerBoundScore, upperBoundScore, colorPalette),
      [lowerBoundScore, upperBoundScore],
    );
    const scoreIsEmpty = Number.isNaN(parseInt(value?.toString(), 10));

    const defaultTitles = useMemo(
      () => titles(metric, lowerBoundScore, upperBoundScore, t),
      [metric, lowerBoundScore, upperBoundScore, t],
    );

    const handleChange = useCallback(
      (score) => onChange?.(parseInt(score, 10)),
      [onChange],
    );

    return (
      <Box
        {...rest}
        flexDirection={'row' as const}
        flexWrap="wrap"
        justifyContent="center"
        maxWidth="100%"
      >
        <Box
          width={1}
          flexGrow={1}
          flexShrink={0}
          flexBasis="auto"
          flexDirection={'row' as const}
          flexWrap={convertResponsiveValue(['wrap-reverse', 'wrap'])}
          justifyContent="center"
          order={convertResponsiveValue([0, -1])}
        >
          {choices?.map((choice, index) =>
            skin === 'Boxes' ? (
              <BoxButton
                key={choice.id}
                color={colorStyle}
                inactiveColor={
                  scoreIsEmpty ? '#ffffff' : theme.colors.separator
                }
                score={index + lowerBoundScore}
                name={name}
                displayValue={choice.title || defaultTitles[index]}
                innerRef={ref}
                colored={false}
                first={index === 0}
                last={index === choices.length - 1}
                isSelected={index + lowerBoundScore === value}
                onChange={handleChange}
              />
            ) : (
              <BoxButton
                key={choice.id}
                color={boxes[index]?.color}
                inactiveColor={
                  scoreIsEmpty ? boxes[index]?.color : theme.colors.separator
                }
                score={index + lowerBoundScore}
                name={name}
                displayValue={choice.title || defaultTitles[index]}
                innerRef={ref}
                colored
                first={index === 0}
                last={index === choices.length - 1}
                isSelected={index + lowerBoundScore === value}
                onChange={handleChange}
              />
            ),
          )}
        </Box>
      </Box>
    );
  },
);

export default BoxesControl;
